import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, InputAdornment, MenuItem, Paper, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Context } from '../../../App';
import SeoHelmet from '../../common/SeoHelmet';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import MuiSnackbar from '../../common/MuiSnackbar';
import PriebeAreaRatioSketch from './PriebeAreaRatioSketch';
import PriebeResult from './PriebeResult';
import { CalculationContextProvider, useCalculationContext } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';

const PHI_OPTIONS = [35, 37.5, 40, 42.5, 45];
const AREA_RATIO_GRID_OPTIONS = [
  { value: 'triangular', label: 'Triangular grid' },
  { value: 'square', label: 'Square grid' },
];

const buildLayer = (index, z1) => ({
  id: index + 1,
  z1,
  z2: '',
  hi: '',
  Ds: '',
  gamma: '',
  phiS: '',
  cS: '',
});

const synchronizeLayers = (layers) => {
  let currentTop = 0;

  return layers.map((layer, index) => {
    const z2Value = layer.z2 === '' ? '' : Number(layer.z2);
    const hiValue = Number.isFinite(z2Value) && z2Value > currentTop ? z2Value - currentTop : '';
    const nextLayer = {
      ...layer,
      id: index + 1,
      z1: currentTop,
      hi: hiValue === '' ? '' : hiValue,
    };

    if (Number.isFinite(z2Value) && z2Value > currentTop) {
      currentTop = z2Value;
    }

    return nextLayer;
  });
};

const hasMissingLayerParams = (layers) =>
  layers.some((layer) =>
    [layer.z2, layer.Ds, layer.gamma, layer.phiS, layer.cS].some((value) => value === '' || value === null || value === undefined)
  );

const hasLayerOrderError = (layers) =>
  layers.some((layer) => {
    const z1 = Number(layer.z1);
    const z2 = Number(layer.z2);
    return !Number.isFinite(z1) || !Number.isFinite(z2) || z2 <= z1;
  });

const formatNumber = (value, digits = 3) => (Number.isFinite(value) ? Number(value).toFixed(digits) : '');
const deriveSpacingFromAreaRatio = (areaRatioValue, diameterValue, gridType) => {
  const areaRatioNumber = Number(areaRatioValue);
  const diameterNumber = Number(diameterValue);

  if (!Number.isFinite(areaRatioNumber) || areaRatioNumber <= 0 || !Number.isFinite(diameterNumber) || diameterNumber <= 0) {
    return '';
  }

  const columnArea = (Math.PI * diameterNumber * diameterNumber) / 4;
  const tributaryArea = areaRatioNumber * columnArea;
  const spacing =
    gridType === 'square' ? Math.sqrt(tributaryArea) : Math.sqrt((2 * tributaryArea) / Math.sqrt(3));

  return Number.isFinite(spacing) && spacing > 0 ? String(Number(spacing.toFixed(4))) : '';
};

const labelWithHelp = (labelText, helpText) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.375,
      px: 0.35,
      py: 0.05,
      mr: 0.25,
      borderRadius: 0.5,
      backgroundColor: 'background.paper',
      whiteSpace: 'nowrap',
      lineHeight: 1.1,
      position: 'relative',
      zIndex: 1,
    }}
  >
    <Box component="span" sx={{ lineHeight: 1.1 }}>
      {labelText}
    </Box>
    <Tooltip title={helpText} arrow placement="top" enterTouchDelay={0}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
      </Box>
    </Tooltip>
  </Box>
);

const interactiveLabelProps = {
  sx: {
    pointerEvents: 'auto',
  },
};

const Priebe = () => {
  const { theme: appTheme } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  const numberInputProps = useMemo(
    () => ({
      style: { colorScheme: appTheme === 'dark' ? 'dark' : 'light' },
    }),
    [appTheme],
  );

  const mergeNumberInputProps = (inputProps = {}) => ({
    inputMode: 'decimal',
    ...inputProps,
    style: {
      ...numberInputProps.style,
      ...(inputProps?.style || {}),
    },
  });

  const [name, setName] = useState('');
  const [areaRatioGridType, setAreaRatioGridType] = useState('triangular');
  const [columnSpacing, setColumnSpacing] = useState('');
  const [columnDiameter, setColumnDiameter] = useState('');
  const [treatmentDepth, setTreatmentDepth] = useState('');
  const [appliedPressure, setAppliedPressure] = useState('');
  const [columnFrictionAngle, setColumnFrictionAngle] = useState(40);
  const [columnModulus, setColumnModulus] = useState('');
  const [footingType, setFootingType] = useState('unlimitedArea');
  const [footingWidth, setFootingWidth] = useState('');
  const [footingLength, setFootingLength] = useState('');
  const [numberOfColumns, setNumberOfColumns] = useState('');
  const [numberOfRows, setNumberOfRows] = useState('');
  const [embedmentDepth, setEmbedmentDepth] = useState('0');
  const [embedmentUnitWeight, setEmbedmentUnitWeight] = useState('0');
  const [layersFront, setLayersFront] = useState([buildLayer(0, 0)]);
  const [inputTabValue, setInputTabValue] = useState(0);

  const setInputs = (inputs) => {
    setName(inputs.name || '');
    const nextGridType = inputs.areaRatioGridType || 'triangular';
    setAreaRatioGridType(nextGridType);
    setColumnSpacing(inputs.columnSpacing ?? deriveSpacingFromAreaRatio(inputs.areaRatio, inputs.columnDiameter, nextGridType));
    setColumnDiameter(inputs.columnDiameter ?? '');
    setTreatmentDepth(inputs.treatmentDepth ?? '');
    setAppliedPressure(inputs.appliedPressure ?? '');
    setColumnFrictionAngle(inputs.columnFrictionAngle ?? 40);
    setColumnModulus(inputs.columnModulus ?? '');
    setFootingType(inputs.footingType || 'unlimitedArea');
    setFootingWidth(inputs.footingWidth ?? '');
    setFootingLength(inputs.footingLength ?? '');
    setNumberOfColumns(inputs.numberOfColumns ?? '');
    setNumberOfRows(inputs.numberOfRows ?? '');
    setEmbedmentDepth(inputs.embedmentDepth ?? '0');
    setEmbedmentUnitWeight(inputs.embedmentUnitWeight ?? '0');
    setLayersFront(synchronizeLayers(Array.isArray(inputs.layersFront) && inputs.layersFront.length > 0 ? inputs.layersFront : [buildLayer(0, 0)]));
  };

  const areaRatioHelper = useMemo(() => {
    const diameter = Number(columnDiameter);
    const spacing = Number(columnSpacing);
    const hasValidDiameter = Number.isFinite(diameter) && diameter > 0;
    const hasValidSpacing = Number.isFinite(spacing) && spacing > 0;
    const formula = areaRatioGridType === 'square' ? 'A = s²' : 'A = (√3 / 2) · s²';

    if (!hasValidDiameter || !hasValidSpacing) {
      return {
        formula,
        hasValidDiameter,
        hasValidSpacing,
        canCompute: false,
        canUseForCalculation: false,
        spacingGreaterThanDiameter: false,
        isWithinChartRange: false,
      };
    }

    const columnArea = (Math.PI * diameter * diameter) / 4;
    const tributaryArea = areaRatioGridType === 'square' ? spacing * spacing : (Math.sqrt(3) / 2) * spacing * spacing;
    const calculatedAreaRatio = tributaryArea / columnArea;
    const replacementRatio = columnArea / tributaryArea;
    const spacingRatio = spacing / diameter;
    const spacingGreaterThanDiameter = spacing > diameter;

    return {
      formula,
      hasValidDiameter,
      hasValidSpacing,
      canCompute: true,
      canUseForCalculation: spacingGreaterThanDiameter,
      columnArea,
      tributaryArea,
      calculatedAreaRatio,
      replacementRatio,
      spacingRatio,
      spacingGreaterThanDiameter,
      isWithinChartRange: calculatedAreaRatio >= 1 && calculatedAreaRatio <= 10,
    };
  }, [areaRatioGridType, columnDiameter, columnSpacing]);

  const effectiveAreaRatio =
    areaRatioHelper.canUseForCalculation && Number.isFinite(areaRatioHelper.calculatedAreaRatio)
      ? String(Number(areaRatioHelper.calculatedAreaRatio.toFixed(4)))
      : '';

  const calculationInput = {
    name,
    areaRatio: effectiveAreaRatio,
    areaRatioGridType,
    columnSpacing: columnSpacing || 0,
    columnDiameter,
    treatmentDepth,
    appliedPressure,
    columnFrictionAngle,
    columnModulus,
    footingType,
    footingWidth: footingType === 'unlimitedArea' ? 0 : footingWidth || 0,
    footingLength: footingType === 'singleFooting' ? footingLength || footingWidth || 0 : 0,
    numberOfColumns: footingType === 'singleFooting' ? numberOfColumns || 0 : 0,
    numberOfRows: footingType === 'stripFooting' ? numberOfRows || 0 : 0,
    embedmentDepth: embedmentDepth || 0,
    embedmentUnitWeight: embedmentUnitWeight || 0,
    layersFront,
  };

  const URL = {
    CALCULATE: '/api/v1/calculations/priebe',
    LOADONE: '/api/v1/calculations/priebe/loadOne/',
    LOADALL: '/api/v1/calculations/priebe/loadAll',
    UPDATE: '/api/v1/calculations/priebe/update/',
    SAVE: '/api/v1/calculations/priebe/save',
    CHECKUNIQUENAME: '/api/v1/calculations/priebe/checkUniqueName/',
    DELETE: '/api/v1/calculations/priebe/delete',
  };

  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const layersMissingParams = useMemo(() => hasMissingLayerParams(layersFront), [layersFront]);
  const layerOrderError = useMemo(() => hasLayerOrderError(layersFront), [layersFront]);
  const serializedLayers = useMemo(() => JSON.stringify(layersFront), [layersFront]);

  const footingSpecificMissing =
    (footingType === 'singleFooting' && (!footingWidth || !numberOfColumns)) ||
    (footingType === 'stripFooting' && (!footingWidth || !numberOfRows));
  const areaRatioOutOfRange = areaRatioHelper.canCompute && !areaRatioHelper.isWithinChartRange;
  const generalMissing =
    !name ||
    !effectiveAreaRatio ||
    !columnDiameter ||
    !columnSpacing ||
    areaRatioOutOfRange ||
    !treatmentDepth ||
    !appliedPressure ||
    !columnModulus ||
    layersFront.length < 1;

  useEffect(() => {
    const shouldDisable = generalMissing || footingSpecificMissing || layersMissingParams || layerOrderError;
    setCalculateButtonDisabled(shouldDisable);
    setSaveButtonDisabled(shouldDisable);
  }, [
    generalMissing,
    footingSpecificMissing,
    layersMissingParams,
    layerOrderError,
    setCalculateButtonDisabled,
    setSaveButtonDisabled,
  ]);

  useEffect(() => {
    setShowOutput(false);
  }, [
    setShowOutput,
    areaRatioGridType,
    columnSpacing,
    columnDiameter,
    treatmentDepth,
    appliedPressure,
    columnFrictionAngle,
    columnModulus,
    footingType,
    footingWidth,
    footingLength,
    numberOfColumns,
    numberOfRows,
    embedmentDepth,
    embedmentUnitWeight,
    serializedLayers,
  ]);

  const addLayer = (event) => {
    event.preventDefault();
    setLayersFront((prev) => synchronizeLayers([...prev, buildLayer(prev.length, Number(prev[prev.length - 1]?.z2 || 0))]));
  };

  const removeLayer = (event) => {
    event.preventDefault();
    setLayersFront((prev) => synchronizeLayers(prev.length > 1 ? prev.slice(0, -1) : prev));
  };

  const handleLayerChange = (index, field, value) => {
    setLayersFront((prev) => {
      const next = prev.map((layer, layerIndex) => (layerIndex === index ? { ...layer, [field]: value } : layer));
      return synchronizeLayers(next);
    });
  };

  const createNewCalculation = () => {
    setName('');
    setAreaRatioGridType('triangular');
    setColumnSpacing('');
    setColumnDiameter('');
    setTreatmentDepth('');
    setAppliedPressure('');
    setColumnFrictionAngle(40);
    setColumnModulus('');
    setFootingType('unlimitedArea');
    setFootingWidth('');
    setFootingLength('');
    setNumberOfColumns('');
    setNumberOfRows('');
    setEmbedmentDepth('0');
    setEmbedmentUnitWeight('0');
    setLayersFront([buildLayer(0, 0)]);
    setShowOutput(false);
  };

  const handleChangeName = (event) => {
    if (event.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(event.target.value);
  };

  const handleInputTabChange = (_, newValue) => {
    setInputTabValue(newValue);
  };

  return (
    <>
      <SeoHelmet
        title="GeoCalc | Priebe Vibro Replacement"
        description="Calculate Priebe vibro replacement improvement factors, footing settlements, and bearing-capacity effects for stone columns."
        url="https://www.geocalc.app/calculations/priebe"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Priebe Vibro Replacement', url: 'https://www.geocalc.app/calculations/priebe' },
        ]}
      />

      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon color="primary" fontSize="large" className="sidebar-open-icon" onClick={handleSidebarDisplay} />
        )}

        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />

        <div className="right-calculation">
          <section className="input loadingRelative">
            {(isLoadingCalculate || isLoadingLoad || isLoadingSave) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress />
              </Box>
            )}

            <Typography variant="h4" sx={{ mb: 2 }}>
              Priebe Vibro Replacement
            </Typography>

            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Tabs
                value={inputTabValue}
                onChange={handleInputTabChange}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ px: 1, pt: 1, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Tab label="General" sx={{ textTransform: 'none' }} />
                <Tab label="Area Ratio" sx={{ textTransform: 'none' }} />
                <Tab label="Footing" sx={{ textTransform: 'none' }} />
                <Tab label="Soil Layers" sx={{ textTransform: 'none' }} />
              </Tabs>

              <Stack spacing={2} sx={{ p: 2 }}>
                {inputTabValue === 0 && (
                  <>
                    <Typography variant="h6">General</Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        size="small"
                        label={labelWithHelp('Name', `Calculation name used for save/load. Unit: none. Maximum length: ${MAX_NAME_LENGTH} characters.`)}
                        InputLabelProps={interactiveLabelProps}
                        value={name}
                        inputProps={{ maxLength: `${MAX_NAME_LENGTH}` }}
                        onChange={handleChangeName}
                        disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                      />
                      <TextField
                        size="small"
                        label={labelWithHelp('Treatment Depth d', 'Depth of the vibro replacement treatment below foundation level considered in the Priebe method. Unit: m.')}
                        InputLabelProps={interactiveLabelProps}
                        value={treatmentDepth}
                        onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Treatment Depth', setTreatmentDepth)}
                        InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                      />
                      <TextField
                        size="small"
                        label={labelWithHelp('Applied Pressure p', 'Average applied foundation pressure used in settlement and depth-factor calculations. Unit: kPa.')}
                        InputLabelProps={interactiveLabelProps}
                        value={appliedPressure}
                        onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Applied Pressure', setAppliedPressure)}
                        InputProps={{ endAdornment: <InputAdornment position="end">kPa</InputAdornment> }}
                      />
                      <TextField
                        size="small"
                        select
                        label={labelWithHelp('Column Friction Angle φc', 'Internal friction angle assigned to the stone column material for the Priebe charts. Unit: degrees.')}
                        InputLabelProps={interactiveLabelProps}
                        value={columnFrictionAngle}
                        onChange={(event) => setColumnFrictionAngle(Number(event.target.value))}
                      >
                        {PHI_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}°
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        label={labelWithHelp('Column Modulus Dc', 'Constrained modulus of the stone column material. Unit: MN/m².')}
                        InputLabelProps={interactiveLabelProps}
                        value={columnModulus}
                        onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Column Modulus', setColumnModulus)}
                        InputProps={{ endAdornment: <InputAdornment position="end">MN/m²</InputAdornment> }}
                      />
                    </Box>
                  </>
                )}

                {inputTabValue === 1 && (
                  <>
                    <Typography variant="h6">Area Ratio</Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          lg: 'minmax(280px, 0.68fr) minmax(0, 1.42fr)',
                        },
                        gap: 2,
                        alignItems: 'start',
                      }}
                    >
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Define the grid geometry here. All derived area-ratio outputs are shown on the sketch panel to the right.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                md: '1fr',
                              },
                              gap: 1.5,
                            }}
                          >
                            <TextField
                              type="number"
                              size="small"
                              label={labelWithHelp('Column Diameter D', 'Diameter of the stone column used in the treatment layout and area-ratio helper. Unit: m.')}
                              InputLabelProps={interactiveLabelProps}
                              value={columnDiameter}
                              onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Column Diameter', setColumnDiameter)}
                              inputProps={mergeNumberInputProps({ min: 0.2, step: 0.2 })}
                              InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                            />
                            <TextField
                              size="small"
                              select
                              label={labelWithHelp('Grid Type', 'Plan layout used to compute tributary area per column. Unit: none. Square uses A = s², triangular uses A = (√3/2)·s².')}
                              InputLabelProps={interactiveLabelProps}
                              value={areaRatioGridType}
                              onChange={(event) => setAreaRatioGridType(event.target.value)}
                            >
                              {AREA_RATIO_GRID_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              type="number"
                              size="small"
                              label={labelWithHelp('Center-to-center Spacing s', 'Spacing between neighboring stone column centers in the selected grid. Unit: m. Must be greater than D.')}
                              InputLabelProps={interactiveLabelProps}
                              value={columnSpacing}
                              onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Column Spacing', setColumnSpacing)}
                              inputProps={mergeNumberInputProps({ min: 0.5, step: 0.5 })}
                              InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                            />
                          </Box>

                          <TextField
                            size="small"
                            label={labelWithHelp('Area Ratio A/Ac', 'Tributary area divided by stone column area. Unit: dimensionless. It is calculated automatically from grid type, spacing, and column diameter.')}
                            InputLabelProps={interactiveLabelProps}
                            value={effectiveAreaRatio}
                            helperText="Calculated result. Recommended chart display range is 1 to 10."
                            disabled
                          />

                          {areaRatioHelper.canCompute && !areaRatioHelper.isWithinChartRange && (
                            <Alert severity="warning" variant="outlined" sx={{ textAlign: 'left', alignItems: 'flex-start' }}>
                              Calculated `A/Ac = {formatNumber(areaRatioHelper.calculatedAreaRatio, 3)}` lies outside the chart display
                              range of `1` to `10`.
                            </Alert>
                          )}

                          {areaRatioHelper.canCompute && !areaRatioHelper.spacingGreaterThanDiameter && (
                            <Alert severity="error" variant="outlined" sx={{ textAlign: 'left', alignItems: 'flex-start' }}>
                              Center-to-center spacing `s` must be greater than column diameter `D` for a valid stone-column layout.
                            </Alert>
                          )}
                        </Stack>
                      </Paper>

                      <Paper variant="outlined" sx={{ px: 2, pb: 2, pt: 1 }}>
                        <Stack spacing={2}>
                          {!areaRatioHelper.canCompute ? (
                            <Alert severity="info" variant="outlined">
                              Enter a positive column diameter `D` and center-to-center spacing `s` to calculate the area ratio from
                              the layout.
                            </Alert>
                          ) : null}

                          <PriebeAreaRatioSketch
                            gridType={areaRatioGridType}
                            columnSpacing={columnSpacing}
                            columnDiameter={columnDiameter}
                            areaRatioHelper={areaRatioHelper}
                            effectiveAreaRatio={effectiveAreaRatio}
                          />
                        </Stack>
                      </Paper>
                    </Box>
                  </>
                )}

                {inputTabValue === 2 && (
                  <>
                    <Typography variant="h6">Footing</Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: footingType === 'singleFooting' ? 'repeat(4, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        size="small"
                        select
                        label={labelWithHelp(
                          'Footing Case',
                          'Select whether the Priebe calculation is for an unlimited treated area, a single footing, or a strip footing. Unit: none.'
                        )}
                        InputLabelProps={interactiveLabelProps}
                        value={footingType}
                        onChange={(event) => setFootingType(event.target.value)}
                      >
                        <MenuItem value="unlimitedArea">Unlimited area</MenuItem>
                        <MenuItem value="singleFooting">Single footing</MenuItem>
                        <MenuItem value="stripFooting">Strip footing</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        label={labelWithHelp('Embedment Depth', 'Depth from ground surface to the foundation base used for overburden in the footing branch. Unit: m.')}
                        InputLabelProps={interactiveLabelProps}
                        value={embedmentDepth}
                        onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Embedment Depth', setEmbedmentDepth)}
                        InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                      />
                      <TextField
                        size="small"
                        label={labelWithHelp(
                          'Unit Weight Above Base',
                          'Average unit weight of soil above the foundation base used to compute overburden stress. Unit: kN/m³.'
                        )}
                        InputLabelProps={interactiveLabelProps}
                        value={embedmentUnitWeight}
                        onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Embedment Unit Weight', setEmbedmentUnitWeight)}
                        InputProps={{ endAdornment: <InputAdornment position="end">kN/m³</InputAdornment> }}
                      />
                      {footingType !== 'unlimitedArea' && (
                        <>
                          <TextField
                            size="small"
                            label={labelWithHelp('Footing Width B', 'Width of the footing or strip footing used in the footing settlement and bearing-capacity branch. Unit: m.')}
                            InputLabelProps={interactiveLabelProps}
                            value={footingWidth}
                            onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Footing Width', setFootingWidth)}
                            InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                          />
                          {footingType === 'singleFooting' && (
                            <TextField
                              size="small"
                              label={labelWithHelp('Footing Length L', 'Length of the single footing used in plan-area and fictitious-width calculations. Unit: m.')}
                              InputLabelProps={interactiveLabelProps}
                              value={footingLength}
                              onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Footing Length', setFootingLength)}
                              InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                            />
                          )}
                          {footingType === 'singleFooting' ? (
                            <TextField
                              size="small"
                              label={labelWithHelp('No. of Columns', 'Number of stone columns contributing under a single footing for the Figure 6 settlement reduction. Unit: count.')}
                              InputLabelProps={interactiveLabelProps}
                              value={numberOfColumns}
                              onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Number of Columns', setNumberOfColumns)}
                            />
                          ) : (
                            <TextField
                              size="small"
                              label={labelWithHelp('No. of Rows', 'Number of stone-column rows beneath the strip footing for the Figure 7 settlement reduction. Unit: count.')}
                              InputLabelProps={interactiveLabelProps}
                              value={numberOfRows}
                              onChange={(event) => onChangeWarningForPositiveDecimals(event, 'Number of Rows', setNumberOfRows)}
                            />
                          )}
                        </>
                      )}
                    </Box>
                    {footingType === 'unlimitedArea' && (
                      <Alert severity="info" variant="outlined">
                        Unlimited-area mode skips the Figure 6/7 footing settlement reduction and the footing bearing-capacity branch.
                      </Alert>
                    )}
                  </>
                )}

                {inputTabValue === 3 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6">Soil Layers Below Foundation Base</Typography>
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" size="small" onClick={addLayer}>
                          Add Layer
                        </Button>
                        <Button variant="outlined" size="small" color="inherit" onClick={removeLayer} disabled={layersFront.length <= 1}>
                          Remove Layer
                        </Button>
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '0.8fr 0.8fr 0.8fr 1fr 1fr 1fr 1fr', gap: 1, pb: 1, fontWeight: 700 }}>
                      <Box>{labelWithHelp('z1', 'Top depth of the soil layer measured downward from foundation base. For the first layer this is typically 0. Unit: m.')}</Box>
                      <Box>{labelWithHelp('z2', 'Bottom depth of the soil layer measured downward from foundation base. Unit: m.')}</Box>
                      <Box>{labelWithHelp('hi', 'Layer thickness. Computed as z2 - z1. Unit: m.')}</Box>
                      <Box>{labelWithHelp('Ds', 'Constrained modulus of the soil layer used in Priebe settlement calculations. Unit: MN/m².')}</Box>
                      <Box>{labelWithHelp('γ', 'Total unit weight of the soil layer used for overburden calculations. Unit: kN/m³.')}</Box>
                      <Box>{labelWithHelp('φs', 'Friction angle of the surrounding soil layer. Unit: degrees.')}</Box>
                      <Box>{labelWithHelp('cs', 'Cohesion of the surrounding soil layer. Unit: kPa.')}</Box>
                    </Box>

                    <Stack spacing={1}>
                      {layersFront.map((layer, index) => (
                        <Box key={layer.id} sx={{ display: 'grid', gridTemplateColumns: '0.8fr 0.8fr 0.8fr 1fr 1fr 1fr 1fr', gap: 1 }}>
                          <TextField size="small" value={layer.z1} disabled />
                          <TextField
                            size="small"
                            value={layer.z2}
                            onChange={(event) =>
                              onChangeWarningForPositiveDecimals(event, 'Layer z2', (value) => handleLayerChange(index, 'z2', value))
                            }
                          />
                          <TextField size="small" value={layer.hi} disabled />
                          <TextField
                            size="small"
                            value={layer.Ds}
                            onChange={(event) =>
                              onChangeWarningForPositiveDecimals(event, 'Layer Ds', (value) => handleLayerChange(index, 'Ds', value))
                            }
                            placeholder="MN/m²"
                          />
                          <TextField
                            size="small"
                            value={layer.gamma}
                            onChange={(event) =>
                              onChangeWarningForPositiveDecimals(event, 'Layer Unit Weight', (value) => handleLayerChange(index, 'gamma', value))
                            }
                            placeholder="kN/m³"
                          />
                          <TextField
                            size="small"
                            value={layer.phiS}
                            onChange={(event) =>
                              onChangeWarningForPositiveDecimals(event, 'Layer Friction Angle', (value) => handleLayerChange(index, 'phiS', value))
                            }
                            placeholder="deg"
                          />
                          <TextField
                            size="small"
                            value={layer.cS}
                            onChange={(event) =>
                              onChangeWarningForPositiveDecimals(event, 'Layer Cohesion', (value) => handleLayerChange(index, 'cS', value))
                            }
                            placeholder="kPa"
                          />
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}
              </Stack>
            </Paper>

            {layerOrderError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Each layer bottom depth z2 must be greater than the layer top depth z1.
              </Alert>
            )}

            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2">
                The tool uses Priebe’s original procedure for `n0`, `n1`, `fd`, `n2`, Figures 1 to 8 chart tracing, footing
                settlement reduction, and the fictitious-footing-width bearing-capacity approximation.
              </Typography>
            </Alert>

            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
                layersFrontLength={layersFront.length}
              />
              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
                layersFrontLength={layersFront.length}
              />
            </Stack>
          </section>

          {showOutput && result?.outputs && (
            <section className="output">
              <PriebeResult result={result} />
            </section>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const WrappedPriebe = () => (
  <CalculationContextProvider>
    <Priebe />
  </CalculationContextProvider>
);

export default WrappedPriebe;
